import {
  loadSingleAgent,
  updateAgent,
} from "@/redux/rtk/features/CRM/agent/agentSlice";
import { loadAllCompanyType } from "@/redux/rtk/features/CRM/companyType/companyTypeSlice";
import { loadAllIndustry } from "@/redux/rtk/features/CRM/industry/industrySlice";
import { loadAllStaff } from "@/redux/rtk/features/hrm/user/userSlice";
import { loadAllContactSource } from "@/redux/rtk/features/CRM/contactSource/contactSourceSlice";
import usePermissions from "@/utils/usePermissions";
import { Button, Card, Form, Input, Select, Skeleton } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

export default function CompanyInfo({ data, loading }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [triggerSave, setTriggerSave] = useState(false);
  const dispatch = useDispatch();

  const { list: companyType, loading: companyTypeLoading } = useSelector(
    (state) => state.companyType
  );

  const { list: industryList, loading: industryLoading } = useSelector(
    (state) => state.industry
  );

  const { list: contactSourceList, loading: contactSourceLoading } = useSelector(
    (state) => state.contactSource
  );

  const { permissions } = usePermissions();
  const canEdit = permissions?.includes("update-company");

  const onFinish = async (values) => {
    const formData = {
      ...values,
      companySize: parseInt(values.companySize),
      annualRevenue: parseInt(values.annualRevenue),
    };

    const resp = await dispatch(
      updateAgent({ id: data.id, values: formData })
    );
    if (resp.payload.message === "success") {
      dispatch(loadSingleAgent(data.id));
      setTriggerSave(false);
    }
  };

  const onFinishFailed = () => {
    setTriggerSave(false);
    form.resetFields();
  };

  useEffect(() => {
    dispatch(loadAllCompanyType());
    dispatch(loadAllIndustry());
    dispatch(loadAllContactSource());
  }, [dispatch]);

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        companyName: data.companyName || "",
        website: data.website || "",
        phone: data.phone || "",
        email: data.email || "",
        companyOwnerId: data.companyOwnerId || "",
        companyTypeId: data.companyTypeId || "",
        industryId: data.industryId || "",
        companySize: data.companySize || "",
        annualRevenue: data.annualRevenue || "",
        billingStreet: data.billingStreet || "",
        billingCity: data.billingCity || "",
        billingState: data.billingState || "",
        billingCountry: data.billingCountry || "",
        billingZipCode: data.billingZipCode || "",
        shippingStreet: data.shippingStreet || "",
        shippingCity: data.shippingCity || "",
        shippingState: data.shippingState || "",
        shippingCountry: data.shippingCountry || "",
        shippingZipCode: data.shippingZipCode || "",
        contactPerson: data.contactPerson || "",
        rating: data.rating || "",
        employeesCount: data.employeesCount || "",
        studentsNumber: data.studentsNumber || "",
        accountSourceId: data.accountSourceId || "",
      });
    }
  }, [data, form]);

  return (
    <>
      <Skeleton loading={loading} active>
        <Card
          headStyle={{ display: "none" }}
          bodyStyle={{
            padding: 0,
          }}
          className="border-none shadow-sm">
          {data && (
            <Form
              form={form}
              colon={false}
              disabled={!canEdit}
              layout="vertical"
              onFieldsChange={() => setTriggerSave(true)}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              initialValues={{
                companyName: data.companyName || "",
                website: data.website || "",
                phone: data.phone || "",
                email: data.email || "",
                companyOwnerId: data.companyOwnerId || "",
                companyTypeId: data.companyTypeId || "",
                industryId: data.industryId || "",
                companySize: data.companySize || "",
                annualRevenue: data.annualRevenue || "",
                billingStreet: data.billingStreet || "",
                billingCity: data.billingCity || "",
                billingState: data.billingState || "",
                billingCountry: data.billingCountry || "",
                billingZipCode: data.billingZipCode || "",
                shippingStreet: data.shippingStreet || "",
                shippingCity: data.shippingCity || "",
                shippingState: data.shippingState || "",
                shippingCountry: data.shippingCountry || "",
                shippingZipCode: data.shippingZipCode || "",
                contactPerson: data.contactPerson || "",
                rating: data.rating || "",
                employeesCount: data.employeesCount || "",
                studentsNumber: data.studentsNumber || "",
                accountSourceId: data.accountSourceId || "",
              }}>
              <div className="bg-[#fafafa] dark:bg-gray-800 w-full rounded-lg overflow-hidden">
                {/* Header Section */}
                <div className="border-b border-gray-200 dark:border-gray-600 px-4 sm:px-6 py-4">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                        {t("company.companyDetails")}
                      </h3>
                    </div>
                    {triggerSave && (
                      <Form.Item className="mb-0">
                        <div className="flex items-center gap-2">
                          <Button
                            type="primary"
                            htmlType="submit"
                            size="middle"
                            className="shadow-sm hover:shadow-md transition-shadow">
                            {t("company.saveChanges")}
                          </Button>
                          <Button
                            loading={loading}
                            onClick={onFinishFailed}
                            size="middle"
                            className="shadow-sm hover:shadow-md transition-shadow">
                            {t("common.cancel")}
                          </Button>
                        </div>
                      </Form.Item>
                    )}
                  </div>
                </div>

                {/* Form Content */}
                <div className="p-4 sm:p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                    {/* Basic Information Section */}
                    <div className="space-y-6">
                      <div className="bg-white dark:bg-gray-700 rounded-lg p-4 sm:p-5">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          {t("company.basicInformation")}
                        </h4>
                        <div className="space-y-4">
                          <Form.Item
                            label={t("common.phone")}
                            name="phone"
                            rules={[
                              {
                                pattern: /^\+?[0-9]+$/,
                                message: t("common.numericPlusOnly", "Please enter only numbers and +"),
                              },
                            ]}>
                            <Input className="w-full" size="large" placeholder={t("common.phone")} />
                          </Form.Item>

                          <Form.Item
                            label={t("navigation.companyType")}
                            name={"companyTypeId"}>
                            <Select
                              className="w-full"
                              loading={companyTypeLoading}
                              size="large"
                              placeholder={t("company.selectCompanyTypePlaceholder")}
                              showSearch
                              optionFilterProp="children">
                              {companyType?.map((item) => (
                                <Select.Option key={item.id} value={item.id}>
                                  {item?.companyTypeName}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>

                          <Form.Item label={t("navigation.industry")} name={"industryId"}>
                            <Select
                              className="w-full"
                              loading={industryLoading}
                              size="large"
                              placeholder={t("company.selectIndustryPlaceholder")}
                              showSearch
                              optionFilterProp="children">
                              {industryList?.map((item) => (
                                <Select.Option key={item.id} value={item.id}>
                                  {item?.industryName}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Form.Item
                              label={t("company.employeesCount")}
                              name={"employeesCount"}>
                              <Input
                                className="w-full"
                                size="large"
                                type="number"
                                placeholder={t("company.employeesCount")}
                              />
                            </Form.Item>

                            <Form.Item
                              label={t("company.studentsNumber")}
                              name={"studentsNumber"}>
                              <Input
                                className="w-full"
                                size="large"
                                type="number"
                                placeholder={t("company.studentsNumber")}
                              />
                            </Form.Item>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Form.Item
                              label={t("company.rating")}
                              name={"rating"}>
                              <Select size="large" placeholder={t("company.rating")}>
                                <Select.Option value="Shut Down">{t("common.shutDown")}</Select.Option>
                                <Select.Option value="VIP">{t("common.vip")}</Select.Option>
                                <Select.Option value="Active">{t("common.active")}</Select.Option>
                              </Select>
                            </Form.Item>

                            <Form.Item
                              label={t("company.accountSource")}
                              name={"accountSourceId"}>
                              <Select
                                size="large"
                                loading={contactSourceLoading}
                                placeholder={t("company.accountSource")}
                                showSearch
                                optionFilterProp="children">
                                {contactSourceList?.map((item) => (
                                  <Select.Option key={item.id} value={item.id}>
                                    {item?.contactSourceName}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </div>

                          <Form.Item
                            label={t("company.contactPerson")}
                            name={"contactPerson"}>
                            <Input
                              className="w-full"
                              size="large"
                              placeholder={t("company.contactPerson")}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </div>

                    {/* Address Information Section */}
                    <div className="space-y-6">
                      {/* Address Section (Renamed from Billing Address) */}
                      <div className="bg-white dark:bg-gray-700 rounded-lg p-4 sm:p-5">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          {t("common.address", "Address")}
                        </h4>
                        <div className="space-y-4">
                          <Form.Item
                            label={t("company.streetAddress")}
                            name={"billingStreet"}>
                            <Input
                              className="w-full"
                              size="large"
                              placeholder={t("company.enterBillingStreetAddress")}
                            />
                          </Form.Item>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Form.Item label={t("company.city")} name={"billingCity"}>
                              <Input
                                className="w-full"
                                size="large"
                                placeholder={t("company.city")}
                              />
                            </Form.Item>
                            <Form.Item label={t("company.zipCode")} name={"billingZipCode"}>
                              <Input
                                className="w-full"
                                size="large"
                                placeholder={t("company.zipCode")}
                              />
                            </Form.Item>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Form.Item label={t("company.state")} name={"billingState"}>
                              <Input
                                className="w-full"
                                size="large"
                                placeholder={t("company.state")}
                              />
                            </Form.Item>
                            <Form.Item label={t("company.country")} name={"billingCountry"}>
                              <Input
                                className="w-full"
                                size="large"
                                placeholder={t("company.country")}
                              />
                            </Form.Item>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Card>
      </Skeleton >
    </>
  );
}
